import { L } from '@lib';
import { CalendarStatusStyle, CalendarStatusStyleObjectKey } from '@models';
import { ConfigProvider, Segmented } from 'antd';
import { GREEN } from '../Theme/palette';
import './OrderStatusSelect.css';
import React from 'react';

interface IProps {
  /** The job's status as the server has it (or as picked on the card, not yet saved). */
  status?: string;
  /** The whole control is held, e.g. a draft that only leaves draft by being sent. */
  disabled?: boolean;
  onChange: (status: string) => void;
}

// A job's own status as one of the statuses it is shown as: "To do", "Ready to go" and "Estimate
// approved" are Booked now, "Parts not ordered" / "Parts ordered" are On hold (see OrderStatusSelect).
const shownAs = (status?: string) =>
  status === 'Todo' || status === 'ReadyToGo' || status === 'Approved'
    ? 'Booked'
    : status === 'PartsNotOrdered' || status === 'PartsOrdered'
      ? 'OnHold'
      : status;

const LABELS: Record<string, string> = {
  Approval: L('Estimate'),
  Booked: L('Booked'),
  Draft: L('Draft'),
  InProgress: L('In progress'),
  OnHold: L('On hold'),
  Canceled: L('Cancelled'),
  Completed: L('Completed'),
};

/**
 * The job card's status as a segmented control, the look of the booking drawer's toggles. Booked and
 * On hold are the ones to choose between (Completed has its own "Complete job" button in the header);
 * a job that is in another status (an
 * estimate, a draft, in progress, cancelled) shows it as one more segment, selected, because it is a
 * status the job reaches by its own button (Start, Send estimate, Cancel job), not by a pick. It is
 * not marked disabled (that would grey its white text): it is the selected one, so clicking it does
 * nothing, and no other segment leads to it.
 * The selected segment is the status's tag: pale fill, strong text and outline in its own colour.
 */
export const OrderStatusSegmented: React.FC<IProps> = ({ status, disabled, onChange }) => {
  const current = shownAs(status) ?? 'Booked';
  // Completed is not a choice here: the header's "Complete job" button does it (the same mileage step).
  const choices = ['Booked', 'OnHold'];
  const own = choices.includes(current) ? [] : [current];

  // Where the held one belongs in the order a job moves: before the work, or after it.
  const ordered = ['Approval', 'Draft', 'Booked', 'InProgress', 'OnHold', 'Completed', 'Canceled'].filter(
    (value) => choices.includes(value) || own.includes(value)
  );

  // The pill reads like the status tag on the cards: the group's pale fill, its strong colour for the
  // text and the outline (see OrderStatusSelect.css). In progress is the green of the running badge on
  // the calendar card (the stopwatch is going), not the blue of its status group.
  const style = CalendarStatusStyle[current as CalendarStatusStyleObjectKey];
  const main = current === 'InProgress' ? GREEN.tag : style?.main;
  const pale = current === 'InProgress' ? GREEN.card : style?.card;

  return (
    <ConfigProvider
      theme={{
        // Four segments in a narrow card: tighter sides so "In progress" is not cut short.
        token: { controlPaddingHorizontal: 6 },
        components: {
          Segmented: { itemSelectedBg: pale, itemSelectedColor: main, trackBg: '#f0f0f0' },
        },
      }}
    >
      <Segmented
        block
        // Held without antd's own disabled: that greys the selected segment's white text too. The
        // control is simply not clickable, and the segments the job cannot go to are dimmed.
        className={['order-status-segmented', disabled ? 'order-status-segmented-held' : ''].filter(Boolean).join(' ')}
        style={{ '--status-main': main, ...(disabled && { pointerEvents: 'none' }) } as React.CSSProperties}
        value={current}
        onChange={(value) => !disabled && onChange(String(value))}
        options={ordered.map((value) => ({
          value,
          label: LABELS[value] ?? value,
        }))}
      />
    </ConfigProvider>
  );
};
